import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Check, CircleDashed, Landmark, Lock, Plug } from "lucide-react";
import { EmptyState, ErrorState, LoadingState, TableShell, HeaderRow, TH } from "@/components/PageUi";
import { PageContainer, PageHeader, StatCard } from "@/components/PagePrimitives";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { useBankLineSummary } from "./BankLines";
import { periodTotals, useMonthlySummary, usePeriod, usePeriodLink, type Period } from "./period";
import { PeriodRuler } from "./PeriodRuler";
import { BasisToggle, bankAccountLabel, money, num, today, useBankAccounts, useCurrentBook } from "./shared";
import type { BankLineSummary, Book } from "./types";

/** `intro` is the host's to fill: in a demo, its "Try it yourself" tasks (it sits under the header). */
export function BooksHome({ intro }: { intro?: ReactNode } = {}) {
  const current = useCurrentBook();
  const book = current.data;
  return (
    <PageContainer>
      <PageHeader
        eyebrow="Books"
        title="Books"
        subtitle="Double-entry books built from your invoices, bills, expenses and bank transactions, beside your accounting platform."
      />
      {current.loading && book === undefined ? (
        <LoadingState label="Loading your books" />
      ) : current.error ? (
        <ErrorState message={apiErrorMessage(current.error, "Couldn't load your books.")} onRetry={current.reload} />
      ) : !book ? (
        <>
          {intro}
          <NoBook />
        </>
      ) : book.status === "Setup" ? (
        <>
          {intro}
          <SetupChecklist book={book} />
        </>
      ) : (
        <Dashboard book={book} intro={intro} />
      )}
    </PageContainer>
  );
}

/** S1: what must exist before a book can start, with the way to each. */
/**
 * S1. Whether books can start is the API's call: GET /books/start-dates carries the refusal POST /books would give,
 * so Home and the API can't disagree. Bank is advice, never a gate.
 */
function NoBook() {
  const { books, canManage, links } = useBooks();
  const banks = useBankAccounts();
  const start = useLoad(() => books.GET("/v1/books/start-dates", {}), [books]);

  if (start.loading && !start.data) return <LoadingState label="Checking what your books need" />;
  const blocked = start.data?.blockedReason;

  return (
    <div className="space-y-4">
      {blocked ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Before you start</CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            <Prerequisite
              icon={Plug}
              title="Connect Xero or QuickBooks"
              body={blocked.detail ?? "Books need a connected accounting platform."}
              to={links.accountingConnection}
              cta="Organisation, Accounting"
            />
          </CardContent>
        </Card>
      ) : (
        <EmptyState
          icon={BookOpen}
          title="No books yet"
          action={
            canManage ? (
              <Button asChild>
                <Link to="/books/setup">Set up books</Link>
              </Button>
            ) : undefined
          }
        >
          {canManage
            ? "Pick a start date, confirm the control accounts and bank mapping, import opening balances from your accounting platform, then activate."
            : "An owner or admin can start the books. You will see the trial balance, reports and bank transactions here once they do."}
        </EmptyState>
      )}
      {!banks.loading && banks.accounts.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <BankPrerequisite to={links.bankConnections} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function BankPrerequisite({ to }: { to: string }) {
  return (
    <Prerequisite
      icon={Landmark}
      title="Connect a bank account"
      body="Bank transactions keep the cash side of the books complete, so the bank balance in the books can be checked against the bank."
      to={to}
      cta="Bank connections"
    />
  );
}

function Prerequisite({
  icon: Icon,
  title,
  body,
  to,
  cta,
}: {
  icon: typeof Plug;
  title: string;
  body: string;
  to: string;
  cta: string;
}) {
  return (
    <div className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 @xl:flex-row @xl:items-center @xl:justify-between">
      <div className="flex gap-3">
        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div>
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-sm text-muted-foreground">{body}</p>
        </div>
      </div>
      <Button asChild variant="outline" size="sm" className="shrink-0">
        <Link to={to}>
          {cta} <ArrowRight className="size-4" />
        </Link>
      </Button>
    </div>
  );
}

const STEPS: Array<{ key: "startDate" | "controlAccounts" | "bankMapping" | "openingBalances" | "activated"; label: string }> = [
  { key: "startDate", label: "Start date" },
  { key: "controlAccounts", label: "Control accounts" },
  { key: "bankMapping", label: "Bank mapping" },
  { key: "openingBalances", label: "Opening balances" },
  { key: "activated", label: "Activate" },
];

function SetupChecklist({ book }: { book: Book }) {
  const { canManage } = useBooks();
  const checklist = book.checklist ?? {};
  const next = STEPS.find((s) => !checklist[s.key]);
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">Setting up your books</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Starting {formatCalendarDate(book.startDate ?? "")}, with opening balances at the end of{" "}
            {formatCalendarDate(book.openingBalanceDate ?? "")}.
          </p>
        </div>
        {canManage ? (
          <Button asChild size="sm">
            <Link to="/books/setup">
              {next ? `Continue: ${next.label}` : "Review setup"} <ArrowRight className="size-4" />
            </Link>
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <ol className="grid gap-2 @md:grid-cols-2 @4xl:grid-cols-5">
          {STEPS.map((s, i) => {
            const done = Boolean(checklist[s.key]);
            return (
              <li
                key={s.key}
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
                  done ? "border-emerald-500/30 bg-emerald-500/5" : s === next ? "border-foreground/30" : "text-muted-foreground",
                )}
              >
                {done ? <Check className="size-4 text-emerald-600" /> : <CircleDashed className="size-4" />}
                <span className="text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                {s.label}
              </li>
            );
          })}
        </ol>
        {checklist.missing && checklist.missing.length > 0 ? (
          <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {checklist.missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ) : null}
        {!canManage ? (
          <p className="mt-4 text-sm text-muted-foreground">An owner or admin finishes the setup.</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Dashboard({ book, intro }: { book: Book; intro?: ReactNode }) {
  const { books } = useBooks();
  const tb = useLoad(() => books.GET("/v1/books/current/trial-balance", { params: { query: { Date: today() } } }), [books]);
  const banks = useBankAccounts();
  const { canManage } = useBooks();
  const progress = book.progress;
  const period = usePeriod(book);
  const monthly = useMonthlySummary(period.basis);
  const totals = periodTotals(monthly.data?.months, period);
  const withPeriod = usePeriodLink();
  const figure = (v: number) => (monthly.data ? money(v) : "-");

  const balances = new Map((tb.data?.rows ?? []).map((r) => [r.ledgerAccountId, num(r.debit) - num(r.credit)]));
  const bankById = new Map(banks.accounts.map((a) => [a.id, a]));

  return (
    <div className="space-y-5">
      {book.status === "Closed" ? (
        <p className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
          <Lock className="size-4 text-muted-foreground" />
          {book.closedReason === "PlatformCompanyChanged"
            ? "These books were closed because the organisation connected a different accounting company. They are read-only."
            : `These books were closed${book.closedAt ? ` on ${formatDateTime(book.closedAt)}` : ""}. They are read-only.`}
          {canManage ? (
            <Button asChild size="sm" variant="outline" className="ml-auto">
              <Link to="/books/setup">Start new books</Link>
            </Button>
          ) : null}
        </p>
      ) : null}

      <PeriodRuler period={period} bars={monthly.data?.months} trailing={<BasisToggle period={period} />} />
      {intro}

      {/* The period's figures match the P&L for the same months (D49). */}
      <div className="grid gap-3 @lg:grid-cols-3">
        <Link to={withPeriod("/books/profit-and-loss")} className={CARD_LINK}>
          <StatCard label="Income" value={figure(totals.income)} sub={period.label} />
        </Link>
        <Link to={withPeriod("/books/profit-and-loss")} className={CARD_LINK}>
          <StatCard label="Expenses" value={figure(totals.expenses)} sub={period.label} />
        </Link>
        <Link to={withPeriod("/books/profit-and-loss")} className={CARD_LINK}>
          <StatCard label="Net profit" value={figure(totals.net)} sub={period.label} trend={totals.net < 0 ? "down" : undefined} />
        </Link>
      </div>

      <div className="grid gap-3 @lg:grid-cols-3">
        <StatCard label="Status" value={book.status === "Active" ? "Active" : "Closed"} sub={`Since ${formatCalendarDate(book.startDate ?? "")}`} />
        <Link to={withPeriod("/books/bank-lines?status=Uncategorised")} className={CARD_LINK}>
          <BankLinesStat period={period} />
        </Link>
        <Link to="/books/postings?status=Failed" className={CARD_LINK}>
          <StatCard
            label="Posting issues"
            value={progress ? num(progress.failed) : "-"}
            sub={progress ? `${num(progress.waiting)} waiting, ${num(progress.skipped)} skipped, any period` : undefined}
          />
        </Link>
      </div>

      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          Books against the bank today
          <InfoHint label="About this check">
            Each bank account in the books today beside the balance the bank last reported. They agree once every bank
            transaction is matched or categorised. It ignores the period: a bank balance is always now.
          </InfoHint>
        </h2>
        {(tb.loading && !tb.data) || (banks.loading && !banks.data) ? (
          <LoadingState label="Loading balances" />
        ) : tb.error ? (
          <ErrorState message={apiErrorMessage(tb.error, "Couldn't load the balances.")} onRetry={tb.reload} />
        ) : (book.bankAccounts ?? []).length === 0 ? (
          <EmptyState icon={Landmark} title="No bank accounts in the books" />
        ) : (
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Account in the books</TableHead>
                  <TableHead className={cn(TH, "hidden @3xl:table-cell")}>Bank</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Books</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Bank</TableHead>
                  <TableHead className={cn(TH, "hidden @3xl:table-cell")}>Check</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {(book.bankAccounts ?? []).map((a) => {
                  const bank = a.bankAccountId ? bankById.get(a.bankAccountId) : undefined;
                  const booksBalance = balances.get(a.ledgerAccountId) ?? 0;
                  const bankBalance = bank?.balance != null ? num(bank.balance) : null;
                  const agrees = bankBalance != null && Math.abs(bankBalance - booksBalance) < 0.005;
                  const check = !a.tracked ? (
                    <span className="text-xs text-muted-foreground">Balance not checked against the bank</span>
                  ) : bankBalance == null ? (
                    <span className="text-xs text-muted-foreground">No bank balance yet</span>
                  ) : agrees ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      <Check className="size-3.5" /> Agrees
                    </span>
                  ) : (
                    <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
                      Off by {money(booksBalance - bankBalance)}
                    </span>
                  );
                  return (
                    <TableRow key={a.ledgerAccountId}>
                      <TableCell>
                        <div className="font-medium">{a.code ? `${a.code} · ${a.name}` : a.name}</div>
                        {/* In a narrow pane (chat open) the bank and the check fold under the account. */}
                        <div className="text-xs text-muted-foreground @3xl:hidden">
                          {a.tracked && bank ? bankAccountLabel(bank) : "Not tracked"}
                        </div>
                        <div className="mt-1 @3xl:hidden">{check}</div>
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground @3xl:table-cell">
                        {a.tracked && bank ? bankAccountLabel(bank) : "Not tracked"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{money(booksBalance)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {bankBalance != null ? money(bankBalance) : "-"}
                        {bank?.balanceAsOf ? (
                          <div className="text-xs text-muted-foreground">{formatDateTime(bank.balanceAsOf)}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden @3xl:table-cell">{check}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>
        )}
      </section>
    </div>
  );
}

const CARD_LINK = "rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function BankLinesStat({ period }: { period: Period }) {
  const summary = useBankLineSummary(period);
  const sum = (pick: (a: BankLineSummary) => number | string | undefined) => (summary.data ?? []).reduce((n, a) => n + num(pick(a)), 0);
  return (
    <StatCard
      label="Bank transactions to review"
      value={summary.data ? sum((a) => num(a.uncategorised) + num(a.possibleDuplicate)) : "-"}
      sub={summary.data ? `${sum((a) => a.autoCategorised)} auto-categorised in ${period.label}` : `Dated in ${period.label}`}
    />
  );
}
